/** Epoch milliseconds when this access token expires, or null when it has no `exp` claim. */
export function readJwtExpiryMs(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3 || parts.some(part => part.length === 0)) return null;

  try {
    const payload = JSON.parse(decodeBase64Url(parts[1])) as { exp?: unknown };
    const exp = typeof payload.exp === 'number' ? payload.exp : Number.NaN;
    if (!Number.isFinite(exp)) return null;
    return exp * 1000;
  } catch {
    return null;
  }
}

/**
 * Absolute expiry for a newly issued access token.
 * JWT `exp` wins; otherwise `accessTokenExpiresInSeconds` is counted from `now`.
 */
export function resolveAccessTokenExpiresAtMs(
  token: string,
  expiresInSeconds?: number | null,
  now = Date.now(),
): number | null {
  const fromJwt = readJwtExpiryMs(token);
  if (fromJwt != null) return fromJwt;
  if (typeof expiresInSeconds === 'number' && Number.isFinite(expiresInSeconds) && expiresInSeconds > 0) {
    return now + expiresInSeconds * 1000;
  }
  return null;
}

/**
 * True when the access token must not authorize a session.
 * A JWT with a missing or unreadable `exp` is invalid. Non-JWTs (mock tokens)
 * expire only when a stored absolute expiry is already in the past.
 */
export function isAccessTokenExpired(
  token: string,
  storedExpiresAtMs: number | null,
  now = Date.now(),
): boolean {
  if (isJwtShape(token)) {
    const exp = readJwtExpiryMs(token);
    if (exp == null) return true;
    return exp <= now;
  }
  if (storedExpiresAtMs == null) return false;
  return storedExpiresAtMs <= now;
}

function isJwtShape(token: string): boolean {
  const parts = token.split('.');
  return parts.length === 3 && parts.every(part => part.length > 0);
}

function decodeBase64Url(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/').padEnd(
    segment.length + ((4 - (segment.length % 4)) % 4),
    '=',
  );
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
